/**
 * CreateCouponModal.tsx — form tworzenia kuponu (F7a T7.7).
 *
 * Endpoint: POST /api/admin/coupons
 * Pola: code (uppercase), discountType (percentage|fixed), discountValue,
 *       maxUses?, validFrom?, validUntil?.
 *
 * Walidacja klient-side:
 *   - code: min 3 znaki, uppercase alfanumeryczny [A-Z0-9_-]+.
 *   - discountValue: > 0, dla percentage <= 100.
 *   - validUntil > validFrom (jeśli oba podane).
 *
 * Error mapping 409 CODE_TAKEN → inline error na polu code.
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export type DiscountType = 'percentage' | 'fixed';

export interface CreatedCoupon {
  id: string;
  code: string;
  discountType: DiscountType;
  discountValue: number;
}

export interface CreateCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (coupon: CreatedCoupon) => void;
}

interface FieldState {
  code: string;
  discountType: DiscountType;
  discountValue: string;
  maxUses: string;
  validFrom: string;
  validUntil: string;
}

function emptyFields(): FieldState {
  return {
    code: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    validFrom: '',
    validUntil: '',
  };
}

const FORM_ID = 'create-coupon-form';
const CODE_REGEX = /^[A-Z0-9][A-Z0-9_-]*$/;

export function CreateCouponModal({ open, onClose, onSuccess }: CreateCouponModalProps) {
  const { t } = useTranslation();
  const formId = useId();
  const resolvedFormId = FORM_ID + '-' + formId;

  const [fields, setFields] = useState(emptyFields());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFields(emptyFields());
      setIsSubmitting(false);
      setApiError(null);
      setFieldErrors({});
    }
    prevOpenRef.current = open;
  }, [open]);

  function setField<K extends keyof FieldState>(key: K, value: FieldState[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    if (apiError) setApiError(null);
  }

  function validate(): boolean {
    const errors: Record<string, string> = {};
    const code = fields.code.trim().toUpperCase();

    if (!code) {
      errors.code = t('admin.coupons.modal.errorCodeRequired');
    } else if (code.length < 3 || code.length > 50) {
      errors.code = t('admin.coupons.modal.errorCodeLength');
    } else if (!CODE_REGEX.test(code)) {
      errors.code = t('admin.coupons.modal.errorCodeFormat');
    }

    const value = Number.parseInt(fields.discountValue, 10);
    if (!Number.isFinite(value) || value <= 0) {
      errors.discountValue = t('admin.coupons.modal.errorDiscountValuePositive');
    } else if (fields.discountType === 'percentage' && value > 100) {
      errors.discountValue = t('admin.coupons.modal.errorDiscountValuePercentage');
    }

    if (fields.maxUses.trim()) {
      const max = Number.parseInt(fields.maxUses, 10);
      if (!Number.isFinite(max) || max <= 0) {
        errors.maxUses = t('admin.coupons.modal.errorMaxUsesPositive');
      }
    }

    if (fields.validFrom && fields.validUntil) {
      const from = new Date(fields.validFrom);
      const until = new Date(fields.validUntil);
      if (until.getTime() <= from.getTime()) {
        errors.validUntil = t('admin.coupons.modal.errorValidUntilAfterFrom');
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError(null);

    const payload: Record<string, unknown> = {
      code: fields.code.trim().toUpperCase(),
      discountType: fields.discountType,
      discountValue: Number.parseInt(fields.discountValue, 10),
    };
    if (fields.maxUses.trim()) {
      payload.maxUses = Number.parseInt(fields.maxUses, 10);
    }
    if (fields.validFrom) {
      payload.validFrom = new Date(fields.validFrom).toISOString();
    }
    if (fields.validUntil) {
      payload.validUntil = new Date(fields.validUntil).toISOString();
    }

    try {
      const res = await api.post<{ coupon: CreatedCoupon }>('/admin/coupons', payload);
      onSuccess(res.data.coupon);
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      if (code === 'CODE_TAKEN') {
        setFieldErrors((prev) => ({
          ...prev,
          code: t('admin.coupons.modal.errorCodeTaken'),
        }));
      } else if (code === 'VALIDATION_FAILED') {
        setApiError(t('admin.coupons.modal.errorValidation'));
      } else if (code === 'UNAUTHORIZED' || axErr.response?.status === 401) {
        setApiError(t('admin.coupons.modal.errorUnauthorized'));
      } else {
        setApiError(t('admin.coupons.modal.errorUnknown'));
      }
      setIsSubmitting(false);
    }
  }

  const discountTypeOptions = [
    { value: 'percentage', label: t('admin.coupons.discountType.percentage') },
    { value: 'fixed', label: t('admin.coupons.discountType.fixed') },
  ];

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('admin.coupons.modal.buttonCancel')}
      </Button>
      <Button type="submit" form={resolvedFormId} loading={isSubmitting} disabled={isSubmitting}>
        {isSubmitting
          ? t('admin.coupons.modal.buttonSubmitting')
          : t('admin.coupons.modal.buttonSubmit')}
      </Button>
    </>
  );

  const valueSuffix =
    fields.discountType === 'percentage' ? '%' : t('admin.coupons.modal.valueSuffixFixed');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.coupons.modal.title')}
      description={t('admin.coupons.modal.description')}
      size="md"
      footer={footer}
      closeOnBackdrop={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <form id={resolvedFormId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label={t('admin.coupons.modal.fieldCode')}
          value={fields.code}
          onChange={(e) => setField('code', e.target.value.toUpperCase())}
          error={fieldErrors.code}
          required
          autoComplete="off"
          placeholder="SUMMER2026"
        />

        <FormField label={t('admin.coupons.modal.fieldDiscountType')}>
          <Dropdown
            value={fields.discountType}
            onChange={(v) => setField('discountType', v as DiscountType)}
            options={discountTypeOptions}
          />
        </FormField>

        <div className="flex flex-col gap-1">
          <Input
            label={t('admin.coupons.modal.fieldDiscountValue')}
            type="number"
            min={1}
            max={fields.discountType === 'percentage' ? 100 : undefined}
            value={fields.discountValue}
            onChange={(e) => setField('discountValue', e.target.value)}
            error={fieldErrors.discountValue}
            required
          />
          <span className="text-xs text-text-muted">
            {fields.discountType === 'percentage'
              ? t('admin.coupons.modal.hintPercentage')
              : t('admin.coupons.modal.hintFixed', { suffix: valueSuffix })}
          </span>
        </div>

        <Input
          label={t('admin.coupons.modal.fieldMaxUses')}
          type="number"
          min={1}
          value={fields.maxUses}
          onChange={(e) => setField('maxUses', e.target.value)}
          error={fieldErrors.maxUses}
          placeholder={t('admin.coupons.modal.fieldMaxUsesPlaceholder')}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('admin.coupons.modal.fieldValidFrom')}
            type="date"
            value={fields.validFrom}
            onChange={(e) => setField('validFrom', e.target.value)}
          />
          <Input
            label={t('admin.coupons.modal.fieldValidUntil')}
            type="date"
            value={fields.validUntil}
            onChange={(e) => setField('validUntil', e.target.value)}
            error={fieldErrors.validUntil}
          />
        </div>

        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {apiError}
          </div>
        )}
      </form>
    </Modal>
  );
}
